import moment from 'moment';
import React, { useEffect } from 'react';
import { Col } from 'react-grid-system';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';
import { Input } from 'components/Form/Input';
import { ResponsiveCol, ResponsiveRow } from '../AddEvents/styles';
import { TicketDetail } from './Components/TicketDetail';
import { TicketMoreDetail } from './Components/TicketMoreDetail';

const InputWrapper = styled.div`
  margin-top: 10px;
`;

export const BundleTickets = ({
  handleChange,
  COLOR_CODES,
  ticket,
  tickets,
  index,
  ticketType,
  errors,
  allCategories,
}) => {
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  useEffect(() => {
    const allTickets = [
      ...tickets.admin_tickets,
      ...tickets.admin_multiday_tickets,
      ...tickets.admin_other_tickets,
    ];

    //Check if new ticket was added or removed
    const isDeleted = allTickets.length < ticket.associated_tickets.length;
    if (!isDeleted) {
      allTickets.forEach((mTicket, myIndex) => {
        //check if ticket is already associated with bundle
        const associatedTicket = ticket.associated_tickets.find(
          (t) => t.name === mTicket.name
        );

        if (!associatedTicket) {
          const hasTempId = ticket.associated_tickets.find(
            (t) => t.id === myIndex
          );

          if (!hasTempId) {
            const newAssociatedTicket = {
              name: mTicket.name,
              quantity: '',
              id: myIndex,
            };

            handleChange({
              target: {
                name: `associated_tickets`,
                value: [...ticket.associated_tickets, newAssociatedTicket],
              },
            });
          } else {
            //If it has a temporary id, keep track of the name by updatting it
            const newAssociatedTickets = ticket.associated_tickets.reduce(
              (acc, associatedTicket) => {
                if (associatedTicket.id === myIndex) {
                  return [
                    ...acc,
                    {
                      ...associatedTicket,
                      name: mTicket.name,
                    },
                  ];
                } else return [...acc, associatedTicket];
              },
              []
            );

            handleChange({
              target: {
                name: `associated_tickets`,
                value: newAssociatedTickets,
              },
            });
          }
        }
      });
    } else {
      //If ticket was deleted, remove it from associated tickets
      const newAssociatedTickets = ticket.associated_tickets.reduce(
        (acc, associatedTicket) => {
          const isNotDeleted = allTickets.find(
            (t) => t.name === associatedTicket.name
          );

          if (isNotDeleted) {
            return [...acc, associatedTicket];
          } else {
            return acc;
          }
        },
        []
      );

      handleChange({
        target: {
          name: `associated_tickets`,
          value: newAssociatedTickets,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tickets.admin_tickets,
    tickets.admin_multiday_tickets,
    tickets.admin_other_tickets,
  ]);

  // Set access code in parent component to true if code, code_date or code_time is set after the component is mounted
  useEffect(() => {
    if (ticket.code || ticket.code_date || ticket.code_time) {
      // Do it after a delay to avoid setting the access code to true before the component is mounted
      setTimeout(() => {
        handleChange({
          target: {
            name: `access_code`,
            value: true,
          },
        });
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TicketDetail
        ticketType={ticketType}
        index={index}
        ticket={ticket}
        handleChange={handleChange}
        errors={errors}
        allCategories={allCategories}
        isMobile={isMobile}
        COLOR_CODES={COLOR_CODES}
      />

      {[
        ...tickets.admin_tickets,
        ...tickets.admin_multiday_tickets,
        ...tickets.admin_other_tickets,
      ].map((mTicket, myIndex) => {
        const quantity =
          ticket.associated_tickets.find((t) => t.name === mTicket.name)
            ?.quantity ?? '';

        return (
          <ResponsiveRow
            key={myIndex}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <ResponsiveCol>
              <InputWrapper style={{ maxWidth: '20%' }}>
                <Input
                  style={{ marginRight: '15px', width: '80px' }}
                  id={`${ticketType}.${index}.quantity`}
                  name={`${ticketType}.${index}.quantity`}
                  label="Qty"
                  type="number"
                  placeholder=""
                  onChange={({ target }) => {
                    const newAssociatedTickets =
                      ticket.associated_tickets.reduce((acc, ticket) => {
                        if (ticket.id === myIndex || ticket.id === mTicket.id) {
                          acc.push({
                            ...ticket,
                            quantity: parseInt(target.value),
                          });
                        } else {
                          acc.push(ticket);
                        }

                        return acc;
                      }, []);

                    handleChange({
                      target: {
                        name: `associated_tickets`,
                        value: newAssociatedTickets,
                      },
                    });
                  }}
                  value={quantity}
                />
              </InputWrapper>
            </ResponsiveCol>
            <ResponsiveCol style={{ minWidth: '65%' }}>
              <InputWrapper style={{ maxWidth: '70%' }}>
                <Input
                  style={{
                    marginRight: '15px',
                    width: '10rem',
                    background: 'white',
                  }}
                  id={`${ticketType}.${index}.date`}
                  name={`${ticketType}.${index}.date`}
                  label=""
                  type="text"
                  disabled
                  placeholder="Date"
                  value={
                    (moment(mTicket.start_date, 'MM-DD-YYYY').format(
                      'ddd MMM DD'
                    ) ?? '') +
                    (mTicket.end_date
                      ? moment(mTicket.start_date, 'MM-DD-YYYY').format(
                          'DD'
                        ) !==
                        moment(mTicket.end_date, 'MM-DD-YYYY').format('DD')
                        ? ' - ' +
                          moment(mTicket.end_date, 'MM-DD-YYYY').format('DD')
                        : ''
                      : '')
                  }
                />
              </InputWrapper>
              <InputWrapper>
                <Input
                  style={{ marginRight: '15px', background: 'white' }}
                  id={`${ticketType}.${index}.associative_tickets_name`}
                  name={`${ticketType}.${index}.associative_tickets_name`}
                  label=""
                  type="text"
                  disabled
                  placeholder="Name"
                  value={`${mTicket.name ? mTicket.name : ''} ${
                    mTicket.price ? `- $${mTicket.price}` : ''
                  }`}
                />
              </InputWrapper>
            </ResponsiveCol>
            <Col></Col>
          </ResponsiveRow>
        );
      })}

      <TicketMoreDetail
        handleChange={handleChange}
        ticket={ticket}
        index={index}
        ticketType={ticketType}
        errors={errors}
        isMobile={isMobile}
      />
    </>
  );
};
